import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import './App.css';
import AppContainer from './views/containers';
import AdminPage from './components/admin/AdminPage';
import RisksListPage from './views/RisksListPage';
import PricingPage from './views/PricingPage';
import ContactPage from './views/ContactPage';
import { startScan, scanError } from './lib/actions/scanActions';
import { storeSteamId } from './lib/api/billingApi';
import { trackVisit, getGeoLocale } from './lib/api/trackApi';

// Empeche un double comptage / double appel en dev (React StrictMode monte deux fois).
let visitPinged = false;
let localeChecked = false;

function App() {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  // Langue auto selon le pays de l'IP (FR si pays francophone, EN sinon).
  // Ne s'applique QUE si le visiteur n'a pas deja choisi une langue (manuellement ou via ?lng=).
  useEffect(() => {
    if (localeChecked) return;
    localeChecked = true;
    const hasChosen = localStorage.getItem('kmg_lang_choice');
    const hasQuery = new URLSearchParams(window.location.search).has('lng');
    if (hasChosen || hasQuery) return;
    getGeoLocale()
      .then(({ data }) => {
        if (data && data.locale && data.locale !== i18n.resolvedLanguage) {
          i18n.changeLanguage(data.locale);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const path = window.location.pathname.replace(/\/+$/, '');
  const isAdmin = path === '/admin';
  const isGames = path === '/games';
  const isTarifs = path === '/tarifs';
  const isContact = path === '/contact';

  // Comptabilise la visite une fois par chargement (hors page admin).
  useEffect(() => {
    if (visitPinged || isAdmin) return;
    visitPinged = true;
    trackVisit();
  }, [isAdmin]);

  // Steam renvoie l'utilisateur sur "/?steamId=..." (ou "/?error=steam_auth").
  useEffect(() => {
    if (isAdmin || isGames || isTarifs || isContact) return;

    const params = new URLSearchParams(window.location.search);
    const steamId = params.get('steamId');
    const err = params.get('error');

    if (steamId) {
      storeSteamId(steamId);
      dispatch(startScan(steamId, i18n.resolvedLanguage));
    } else if (err) {
      dispatch(scanError('steam_auth'));
    }

    // On nettoie l'URL pour ne pas relancer un scan au rafraichissement.
    if (steamId || err) {
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SEO par page : titre / description / canonical / robots adaptés à la route.
  useEffect(() => {
    const pages = {
      '/games': {
        title: 'Jeux à surveiller — délistés, serveurs coupés, always-online | KeepMyGames',
        desc: "La liste des jeux vidéo en danger : délistés, serveurs menacés, otages de l'always-online. Découvre lesquels sont sauvables en DRM-free sur GOG.",
        path: '/games',
        index: true,
      },
      '/tarifs': {
        title: 'Passer Pro — alertes & surveillance de ta bibliothèque | KeepMyGames',
        desc: "Sois alerté dès qu'un de tes jeux Steam passe en danger ou baisse de prix sur GOG. Surveillance continue de ta bibliothèque. 4,99€/mois ou 49€/an.",
        path: '/tarifs',
        index: true,
      },
      '/admin': {
        title: 'Administration | KeepMyGames',
        desc: '',
        path: '/admin',
        index: false,
      },
      '/contact': {
        title: 'Contact & support | KeepMyGames',
        desc: 'Une question, un bug, une suggestion ? Contacte le support KeepMyGames.',
        path: '/contact',
        index: true,
      },
      '': {
        title: 'KeepMyGames — Combien de tes jeux Steam vont disparaître ?',
        desc: "Scanne ta bibliothèque Steam et découvre lesquels de tes jeux risquent de disparaître : délistés, serveurs coupés, otages de l'always-online. Obtiens ton score de préservation.",
        path: '/',
        index: true,
      },
    };
    const p = pages[path] || pages[''];
    document.title = p.title;

    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    if (p.desc) setMeta('description', p.desc);
    setMeta('robots', p.index ? 'index, follow, max-image-preview:large' : 'noindex, nofollow');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://keepmygames.com${p.path}`);
  }, [path]);

  if (isAdmin) return <AdminPage />;
  if (isGames) return <RisksListPage />;
  if (isTarifs) return <PricingPage />;
  if (isContact) return <ContactPage />;

  return <AppContainer />;
}

export default App;
